import { useEffect, useMemo, useRef, useState } from "react";
import { isAxiosError } from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowUpDown,
  BookOpen,
  Info,
  Loader2,
  RefreshCw,
  RotateCw,
  Search,
  Sparkles,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FacetedFilter } from "@/components/ui/faceted-filter";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/date-utils";

import {
  CopyableValue,
  GuidanceList,
  GuidanceProse,
  JsonDisclosure,
  SectionHeading,
} from "@/components/intents/guidance-primitives";
import { useDesks } from "@/hooks/use-desk-migration";
import {
  useDeleteIntent,
  useIntents,
  useRepublishItemEvents,
  useSourceCatalogItem,
  useUpdateIntent,
  type CatalogIntent,
  type IntentStatus,
} from "@/hooks/use-intents";

type StatusFilter = "all" | IntentStatus;

/** Sentinel facet value: intents the engine will never build. */
const NEEDS_ATTENTION = "wont_load";

const GUIDANCE_FILTER_OPTIONS = [
  { label: "⚠ Needs attention — won't load", value: NEEDS_ATTENTION },
  { label: "Has “when to use”", value: "when_to_use" },
  { label: "Has “when not to use”", value: "when_not_to_use" },
  { label: "Has guidelines", value: "guidelines" },
  { label: "Has examples", value: "examples" },
];

/**
 * The engine only builds catalog-derived intents with a description
 * (`_build_toolkit` returns None without one, and `build_intent_toolkits`
 * skips rows with no source_ref entirely).
 */
function isUnbuildable(intent: CatalogIntent): boolean {
  return !intent.description || !intent.source_ref;
}

function matchesFacet(intent: CatalogIntent, value: string): boolean {
  if (value === NEEDS_ATTENTION) return isUnbuildable(intent);
  return hasGuidanceSection(intent, value);
}

function freshness(updatedAt: number): string {
  const seconds = Math.max(0, Math.round((Date.now() - updatedAt) / 1000));
  if (seconds < 15) return "just now";
  if (seconds < 60) return `${String(seconds)}s ago`;
  if (seconds < 3600) return `${String(Math.round(seconds / 60))}m ago`;
  return `${String(Math.round(seconds / 3600))}h ago`;
}

function hasText(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function hasGuidanceSection(intent: CatalogIntent, key: string): boolean {
  if (!intent.guidance) return false;
  const value = new Map(Object.entries(intent.guidance)).get(key);
  return Array.isArray(value)
    ? value.length > 0
    : typeof value === "string" && value.trim() !== "";
}

/* ------------------------------------------------------------------ */
/* Small primitives                                                    */
/* ------------------------------------------------------------------ */

function StatusDot({
  status,
  className,
}: {
  status: IntentStatus;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-block size-1.5 shrink-0 rounded-full",
        status === "active"
          ? "bg-emerald-500"
          : "bg-amber-400 dark:bg-amber-500",
        className,
      )}
    />
  );
}

function StatusChip({ status }: { status: IntentStatus }) {
  return (
    <span className="text-muted-foreground inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
      <StatusDot status={status} />
      {status}
    </span>
  );
}

function FlowStep({
  title,
  caption,
}: {
  title: string;
  caption?: React.ReactNode;
}) {
  return (
    <div className="bg-muted/30 rounded-md border px-2.5 py-1.5">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em]">
        {title}
      </p>
      {caption && (
        <p className="text-muted-foreground text-[11px] leading-snug">
          {caption}
        </p>
      )}
    </div>
  );
}

function FlowConnector() {
  return (
    <div aria-hidden className="py-0.5 pl-5">
      <span className="bg-border block h-2.5 w-px" />
    </div>
  );
}

const clip = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;

/** What the agent sees before and after picking this intent. */
function SelectionExplainer({ intent }: { intent: CatalogIntent }) {
  const guidance = intent.guidance ?? {};
  const purpose = intent.description ?? "";
  // Mirrors catalog_description: purpose first, then when_to_use
  // (which the engine falls back to purpose — shown once here).
  const whenToUse =
    typeof guidance.when_to_use === "string" && guidance.when_to_use.trim()
      ? guidance.when_to_use
      : "";
  const boundaries = Array.isArray(guidance.when_not_to_use)
    ? guidance.when_not_to_use
    : [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs underline decoration-dotted underline-offset-2 transition-colors"
        >
          <Info className="size-3" />
          how selection works
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[380px] space-y-2">
        <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.2em]">
          Intent selection
        </p>

        <div>
          <FlowStep
            title="Capability catalog"
            caption={
              <>
                <span className="block">
                  one line per <em>active</em> intent — this one&apos;s (the
                  name is only a toolkit id; the text is what selects):
                </span>
                <span className="bg-background mt-1 block break-words rounded border px-2 py-1 font-mono text-[10.5px] leading-snug">
                  <span className="text-muted-foreground">
                    catalog_intent_…:{" "}
                  </span>
                  <span className="text-foreground/85">
                    {clip(purpose, 90)}
                    {whenToUse && ` ${clip(whenToUse, 90)}`}
                  </span>
                  {boundaries.length > 0 && (
                    <span className="text-destructive block">
                      Not for: {clip(boundaries.join("; "), 110)}
                    </span>
                  )}
                </span>
              </>
            }
          />
          <FlowConnector />
          <FlowStep
            title="Agent picks an intent"
            caption="chooses on those lines alone — nothing else is loaded yet"
          />
          <FlowConnector />
          <FlowStep
            title="Guidance loads"
            caption="the form-and-ticket workflow, plus Guidelines as “along the way” notes"
          />
          <div className="mt-1.5 space-y-1.5 pl-2">
            <div className="flex items-start gap-2 text-[11px] leading-snug">
              <span
                aria-hidden
                className="text-muted-foreground/50 shrink-0 font-mono"
              >
                ├─
              </span>
              <span className="text-emerald-700 dark:text-emerald-400">
                fits&ensp;→&ensp;show the form&ensp;→&ensp;create the ticket
              </span>
            </div>
            <div className="flex items-start gap-2 text-[11px] leading-snug">
              <span
                aria-hidden
                className="text-muted-foreground/50 shrink-0 font-mono"
              >
                └─
              </span>
              <span className="text-amber-700 dark:text-amber-500">
                wrong fit&ensp;⟲&ensp;back to the catalog — the agent picks
                another intent next turn
              </span>
            </div>
          </div>
        </div>

        <p className="text-muted-foreground border-t pt-2 text-[11px] leading-snug">
          <span className="text-destructive font-mono">✕</span> An intent with
          no Description never enters the catalog at all — the engine drops it
          at build time.
        </p>
      </PopoverContent>
    </Popover>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function RerunMigrationButton({ tenantId }: { tenantId: string }) {
  const republish = useRepublishItemEvents(tenantId);

  const onConfirm = () => {
    if (republish.isPending) return;
    republish.mutate(undefined, {
      onSuccess: ({ items, published }) => {
        toast.success(
          `Republished ${String(published)} of ${String(items)} item events. The normalizer processes them in the background.`,
        );
      },
      onError: (err) => {
        if (isAxiosError(err)) {
          const status = err.response?.status;
          const detail = (err.response?.data as { detail?: unknown } | null)
            ?.detail;
          const detailText =
            typeof detail === "string"
              ? detail
              : "some events failed to enqueue";
          if (status === 503) {
            toast.info(
              "The migration queue isn't wired for this environment — nothing was enqueued. On production this is deliberate until the dev migration proves out.",
            );
            return;
          }
          if (status === 502) {
            toast.warning(
              `Partial enqueue — ${detailText}. Re-running is safe and converges.`,
            );
            return;
          }
        }
        toast.error("Failed to republish item events");
      },
    });
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline">
          <RefreshCw
            className={cn("mr-1 size-4", republish.isPending && "animate-spin")}
          />
          Re-run migration
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Re-run the migration for this tenant?
          </AlertDialogTitle>
          <AlertDialogDescription>
            Republishes every <strong>visible</strong> catalog item (hidden,
            discovered, and deleted items are skipped) for background
            re-normalization. Unchanged items are skipped by the fingerprint
            check — but items whose source changed, and every item after a
            normalizer prompt update, are rewritten from scratch:{" "}
            <strong>
              status is recomputed, so intents you already activated can revert
              to draft.
            </strong>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm}>Re-run</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The catalog→intents migration review: list, detail, activate/delete. */
export function CatalogMigrationBrowser({ tenantId }: { tenantId: string }) {
  const {
    data: intents,
    isLoading,
    isLoadingError,
    isError,
    isFetching,
    refetch,
    dataUpdatedAt,
  } = useIntents(tenantId);
  const listRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);

  // Re-render on a slow tick so the freshness label stays honest.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setTick((t) => t + 1);
    }, 10_000);
    return () => {
      clearInterval(id);
    };
  }, []);
  const { data: desks } = useDesks(tenantId);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [guidanceFilters, setGuidanceFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<"name" | "updated">("name");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSource, setShowSource] = useState(true);

  const deskNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const desk of desks ?? []) map.set(desk.id, desk.name);
    return map;
  }, [desks]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (intents ?? [])
      .filter((i) => statusFilter === "all" || i.status === statusFilter)
      .filter(
        (i) =>
          guidanceFilters.length === 0 ||
          guidanceFilters.some((k) => matchesFacet(i, k)),
      )
      .filter((i) => !q || i.name.toLowerCase().includes(q))
      .sort((a, b) =>
        sortBy === "updated"
          ? b.updated_at.localeCompare(a.updated_at)
          : a.name.localeCompare(b.name),
      );
  }, [intents, query, statusFilter, guidanceFilters, sortBy]);

  const selected = intents?.find((i) => i.id === selectedId) ?? null;

  const counts = useMemo(() => {
    const list = intents ?? [];
    const all = list.length;
    const active = list.filter((i) => i.status === "active").length;
    const wontLoad = list.filter(isUnbuildable).length;
    const buildable = all - wontLoad;
    const activeBuildable = list.filter(
      (i) => i.status === "active" && !isUnbuildable(i),
    ).length;
    return {
      all,
      active,
      draft: all - active,
      wontLoad,
      buildable,
      activeBuildable,
    };
  }, [intents]);

  if (isLoading) {
    return (
      <div
        role="status"
        aria-label="Loading intents"
        className="flex h-64 items-center justify-center"
      >
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Only a load that never produced data takes over the panel; a failed
  // background refetch keeps the list visible with a banner instead.
  if (isLoadingError) {
    return (
      <div
        role="alert"
        className="border-destructive/30 text-muted-foreground flex h-56 flex-col items-center justify-center gap-3 rounded-xl border border-dashed text-sm"
      >
        Failed to load intents. Is intents-api reachable through the backoffice
        proxy for this environment?
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
      {/* Intent list */}
      <div className="bg-card flex min-h-0 flex-col overflow-hidden rounded-xl border">
        <div className="flex flex-col gap-2 border-b p-2">
          {isError && (
            <p
              role="alert"
              className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border px-2.5 py-1.5 text-xs"
            >
              Background refresh is failing — showing data from{" "}
              {freshness(dataUpdatedAt)}.
            </p>
          )}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder="Search intents…"
                className="pl-8"
              />
            </div>
            <FacetedFilter
              title="Guidance"
              options={GUIDANCE_FILTER_OPTIONS}
              selectedValues={guidanceFilters}
              onValuesChange={setGuidanceFilters}
              placeholder="Filter guidance…"
              className="shrink-0"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 shrink-0 px-2.5"
                  title={
                    sortBy === "name"
                      ? "Sorted by name (A–Z)"
                      : "Sorted by last update (newest first)"
                  }
                >
                  <ArrowUpDown className="size-4" />
                  <span className="text-muted-foreground text-xs">
                    {sortBy === "name" ? "A–Z" : "Recent"}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                <DropdownMenuRadioGroup
                  value={sortBy}
                  onValueChange={(v) => {
                    setSortBy(v as "name" | "updated");
                  }}
                >
                  <DropdownMenuRadioItem value="name">
                    Name
                  </DropdownMenuRadioItem>
                  <DropdownMenuRadioItem value="updated">
                    Recently updated
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              className="h-9 shrink-0 px-2.5"
              title="Refresh — the migration writes in the background"
              aria-label="Refresh intents"
              onClick={() => {
                if (isFetching) return;
                void refetch();
              }}
            >
              <RotateCw
                className={cn("size-4", isFetching && "animate-spin")}
              />
            </Button>
          </div>
          <Tabs
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
              <TabsTrigger value="draft">Draft ({counts.draft})</TabsTrigger>
              <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="space-y-1 px-1 pb-1">
            {counts.buildable > 0 && (
              <div className="bg-muted h-1 overflow-hidden rounded-full">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-[width] duration-500"
                  style={{
                    width: `${String((counts.activeBuildable / counts.buildable) * 100)}%`,
                  }}
                />
              </div>
            )}
            <p className="text-muted-foreground flex items-center justify-between text-[10px] font-medium uppercase tracking-widest">
              <span>
                {counts.all === 0
                  ? "no intents yet"
                  : `${String(counts.activeBuildable)} of ${String(counts.buildable)} activated`}
                {counts.wontLoad > 0 && (
                  <span className="text-destructive">
                    {" "}
                    · {counts.wontLoad} won&apos;t load
                  </span>
                )}
              </span>
              <span>refreshed {freshness(dataUpdatedAt)}</span>
            </p>
          </div>
        </div>
        <div
          ref={listRef}
          tabIndex={-1}
          className="min-h-0 flex-1 overflow-y-auto p-2 focus:outline-none"
        >
          {filtered.length === 0 ? (
            <p className="text-muted-foreground p-4 text-center text-sm">
              {counts.all === 0
                ? "This tenant has no intents yet."
                : "No intent matches the filter."}
            </p>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {filtered.map((intent) => {
                const isSelected = intent.id === selectedId;
                return (
                  <li key={intent.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(intent.id);
                        requestAnimationFrame(() => {
                          detailRef.current?.focus();
                        });
                      }}
                      aria-current={isSelected}
                      className={cn(
                        "focus-visible:ring-ring relative flex w-full items-center gap-2.5 rounded-lg py-2 pl-3.5 pr-2.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2",
                        isSelected
                          ? "bg-accent text-accent-foreground font-medium"
                          : "hover:bg-accent/60",
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "bg-primary absolute inset-y-1.5 left-0 w-0.5 rounded-full transition-opacity",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <StatusDot status={intent.status} />
                      <span className="sr-only">{intent.status}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{intent.name}</span>
                        <span className="text-muted-foreground block truncate text-xs font-normal">
                          {deskNames.get(intent.desk_id) ?? intent.desk_id}
                        </span>
                      </span>
                      {isUnbuildable(intent) && (
                        <AlertTriangle
                          aria-label="Won't load"
                          className="text-destructive size-3.5 shrink-0"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Detail */}
      <div
        ref={detailRef}
        tabIndex={-1}
        role="region"
        aria-label="Intent details"
        className="bg-card min-h-0 overflow-y-auto rounded-xl border focus:outline-none"
      >
        {selected ? (
          <IntentDetail
            key={selected.id}
            tenantId={tenantId}
            intent={selected}
            deskName={deskNames.get(selected.desk_id)}
            showSource={showSource}
            onToggleSource={setShowSource}
            onDeleted={() => {
              setSelectedId(null);
              requestAnimationFrame(() => {
                listRef.current?.focus();
              });
            }}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
            <span className="border-muted-foreground/30 flex size-12 items-center justify-center rounded-full border border-dashed">
              <Sparkles className="text-muted-foreground size-5" />
            </span>
            <p className="text-sm font-medium">Select an intent</p>
            <p className="text-muted-foreground max-w-sm text-sm">
              Review what the catalog migration produced, then activate the
              intents the agent may offer.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */

function SourceItemPanel({
  tenantId,
  itemId,
}: {
  tenantId: string;
  itemId: string;
}) {
  const {
    data: item,
    isLoading,
    isError,
    refetch,
  } = useSourceCatalogItem(tenantId, itemId);

  return (
    <div className="animate-in fade-in slide-in-from-right-2 bg-muted/20 flex h-fit min-w-0 flex-col gap-4 rounded-lg border p-4 duration-300 xl:sticky xl:top-4">
      <div className="space-y-1">
        <p className="text-muted-foreground flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em]">
          <span
            aria-hidden
            className="size-1.5 animate-pulse rounded-full bg-emerald-500"
          />
          Live source
        </p>
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <BookOpen className="text-muted-foreground size-4 shrink-0" />
          {item ? (
            <span className="flex min-w-0 items-center gap-2">
              {item.icon && <span className="shrink-0">{item.icon}</span>}
              <span className="truncate">{item.name}</span>
            </span>
          ) : (
            "Source catalog item"
          )}
        </h3>
        <p className="text-muted-foreground text-xs">
          The catalog item this intent was derived from — it may have changed
          since the migration ran.
        </p>
      </div>
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 className="text-muted-foreground size-6 animate-spin" />
          </div>
        ) : isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p className="text-destructive">Failed to load the catalog item.</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : item ? (
          <>
            {item.deleted_at && (
              <p className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border px-3 py-2 text-sm">
                This catalog item was deleted on{" "}
                {formatDateTime(item.deleted_at)}.
              </p>
            )}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 border-y py-3 text-sm">
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Type
                </dt>
                <dd>{item.ticket_type}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Priority
                </dt>
                <dd>{item.priority}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Visible
                </dt>
                <dd>{item.is_visible ? "Yes" : "No"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Updated
                </dt>
                <dd>{formatDateTime(item.updated_at)}</dd>
              </div>
            </dl>
            <GuidanceProse title="Description" text={item.description || "—"} />
            {item.form_fields.length > 0 && (
              <section className="space-y-2">
                <SectionHeading>
                  Form fields ({item.form_fields.length})
                </SectionHeading>
                <ul className="space-y-1 text-sm">
                  {item.form_fields.map((field) => (
                    <li
                      key={String(field.id ?? field.label ?? field.type)}
                      className="text-foreground/85 flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {String(field.label ?? field.type ?? "field")}
                        {field.required === true && (
                          <span className="text-destructive"> *</span>
                        )}
                      </span>
                      <span className="text-muted-foreground shrink-0 font-mono text-xs">
                        {String(field.type ?? "")}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

const KNOWN_GUIDANCE_KEYS = new Set([
  "purpose",
  "when_to_use",
  "when_not_to_use",
  "guidelines",
  "examples",
  "deflection",
]);

function IntentDetail({
  tenantId,
  intent,
  deskName,
  showSource,
  onToggleSource,
  onDeleted,
}: {
  tenantId: string;
  intent: CatalogIntent;
  deskName: string | undefined;
  showSource: boolean;
  onToggleSource: (show: boolean) => void;
  onDeleted: () => void;
}) {
  const update = useUpdateIntent(tenantId);
  const remove = useDeleteIntent(tenantId);
  const showSourcePanel = showSource && Boolean(intent.source_ref);
  const unbuildable = isUnbuildable(intent);

  const nextStatus: IntentStatus =
    intent.status === "active" ? "draft" : "active";

  const describeError = (err: unknown, fallback: string): string =>
    isAxiosError(err) && err.response?.status === 403
      ? "Platform-admin role is required to change intents"
      : fallback;

  const onToggleStatus = () => {
    if (update.isPending) return;
    update.mutate(
      { id: intent.id, status: nextStatus },
      {
        onSuccess: () => {
          toast.success(
            nextStatus === "active"
              ? `"${intent.name}" activated`
              : `"${intent.name}" moved back to draft`,
          );
        },
        onError: (err) =>
          toast.error(describeError(err, "Failed to update status")),
      },
    );
  };

  const onDelete = () => {
    if (remove.isPending) return;
    remove.mutate(intent.id, {
      onSuccess: () => {
        toast.success(`"${intent.name}" deleted`);
        onDeleted();
      },
      onError: (err) =>
        toast.error(describeError(err, "Failed to delete intent")),
    });
  };

  const guidance = intent.guidance ?? {};
  const extraGuidance = Object.entries(guidance).filter(
    ([key, value]) => !KNOWN_GUIDANCE_KEYS.has(key) && value != null,
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-1 flex flex-col duration-300">
      {/* Sticky action header */}
      <div className="bg-card/95 sticky top-0 z-10 flex flex-wrap items-start justify-between gap-3 border-b px-6 py-4 backdrop-blur">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2.5">
            <h2 className="font-display truncate text-xl font-semibold tracking-tight">
              {intent.name}
            </h2>
            <StatusChip status={intent.status} />
          </div>
          <p className="text-muted-foreground text-xs">
            {deskName ?? intent.desk_id}
            {" · "}
            {intent.source_ref ? "Catalog-derived" : "Manually authored"}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {intent.source_ref && (
            <Button
              size="sm"
              variant={showSource ? "secondary" : "outline"}
              onClick={() => {
                onToggleSource(!showSource);
              }}
            >
              <BookOpen className="mr-1 size-4" />
              {showSource ? "Hide source" : "Compare source"}
            </Button>
          )}
          <Button
            size="sm"
            variant={intent.status === "active" ? "outline" : "default"}
            onClick={onToggleStatus}
            disabled={
              update.isPending || (nextStatus === "active" && unbuildable)
            }
            title={
              nextStatus === "active" && unbuildable
                ? "This intent can't load, so activating it would change nothing"
                : undefined
            }
          >
            {update.isPending && (
              <Loader2 className="mr-1 size-4 animate-spin" />
            )}
            {intent.status === "active" ? "Deactivate" : "Activate"}
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" aria-label="Delete intent">
                {remove.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Trash2 className="text-destructive size-4" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this intent?</AlertDialogTitle>
                <AlertDialogDescription>
                  &quot;{intent.name}&quot; will be retired and the agent stops
                  offering it. Note: the next migration run will recreate it as
                  a new intent unless the catalog item is hidden or removed —
                  delete is not a suppression mechanism.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={onDelete}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="p-6">
        {unbuildable && (
          <div
            role="alert"
            className="border-destructive/40 bg-destructive/5 mb-6 flex items-start gap-3 rounded-lg border px-4 py-3"
          >
            <AlertTriangle className="text-destructive mt-0.5 size-4 shrink-0" />
            <div className="space-y-0.5 text-sm">
              <p className="text-destructive font-semibold">
                This intent will not load
              </p>
              <p className="text-destructive">
                {!intent.description
                  ? "It has no description, and the engine drops description-less intents at build time — the agent can never select it, even when active. Give the source catalog item a description and re-run the migration (note: re-running recomputes status)."
                  : "It has no source catalog item reference, and the engine only builds catalog-derived intents — the agent can never select it, even when active."}
              </p>
            </div>
          </div>
        )}
        <div className={cn("grid gap-6", showSourcePanel && "xl:grid-cols-2")}>
          <div className="flex min-w-0 flex-col gap-6">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-b pb-4 text-sm sm:grid-cols-4">
              <div className="min-w-0">
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Source ref
                </dt>
                <dd className="font-mono text-xs leading-6">
                  <CopyableValue label="Source ref" value={intent.source_ref} />
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Intent ID
                </dt>
                <dd className="font-mono text-xs leading-6">
                  <CopyableValue label="Intent ID" value={intent.id} />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Created
                </dt>
                <dd className="text-xs leading-6">
                  {formatDateTime(intent.created_at)}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-[10px] font-semibold uppercase tracking-widest">
                  Updated
                </dt>
                <dd className="text-xs leading-6">
                  {formatDateTime(intent.updated_at)}
                </dd>
              </div>
            </dl>

            {intent.description && (
              <GuidanceProse
                title="Description"
                text={intent.description}
                note="the engine reads this as the intent's purpose"
                action={<SelectionExplainer intent={intent} />}
              />
            )}
            {hasText(guidance.purpose) && (
              <GuidanceProse
                title="Purpose"
                text={guidance.purpose}
                note="stored only — not read by the engine"
              />
            )}
            {hasText(guidance.when_to_use) && (
              <GuidanceProse
                title="When to use"
                text={guidance.when_to_use}
                note="its own selection slot — falls back to Description when empty"
              />
            )}
            {Array.isArray(guidance.when_not_to_use) && (
              <GuidanceList
                title="When not to use"
                items={guidance.when_not_to_use}
                tone="negative"
              />
            )}
            {Array.isArray(guidance.guidelines) && (
              <GuidanceList
                title="Guidelines"
                items={guidance.guidelines}
                note="loaded after selection as “along the way” notes"
              />
            )}
            {Array.isArray(guidance.examples) && (
              <GuidanceList
                title="Examples"
                items={guidance.examples}
                note="stored only — not read by the engine"
              />
            )}
            {hasText(guidance.deflection) && (
              <GuidanceProse
                title="Deflection"
                text={guidance.deflection}
                note="stored only — not read by the engine"
              />
            )}

            {extraGuidance.length > 0 && (
              <JsonDisclosure
                title="Other guidance"
                value={Object.fromEntries(extraGuidance)}
                defaultOpen
              />
            )}

            {intent.metadata && (
              <JsonDisclosure
                title="Migration metadata"
                value={intent.metadata}
              />
            )}
          </div>
          {showSourcePanel && intent.source_ref && (
            <SourceItemPanel tenantId={tenantId} itemId={intent.source_ref} />
          )}
        </div>
      </div>
    </div>
  );
}
